import { Button, Form, InputNumber, message, Modal, Space, Table } from "antd";
import { useEffect } from "react";
import {
  TeacherIntervalRate,
  usePayRatesLazyQuery,
  useUpdatePayRatesMutation,
} from "../../../../../../generated/graphql";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

// Make sure this is in sync with the service.
const MAX_PENALTY_RATE = 200;

const hourlyPayRates = [
  { field: "classRate", label: "Class (teaching / covering)" },
  { field: "privatePayRate", label: "Private (teaching / covering)" },
  { field: "assistingRate", label: "Assisting" },
  { field: "trainingRate", label: "Training" },
  { field: "markingHwHelpRate", label: "Marking and Homework Help" },
  { field: "receptionRate", label: "Reception" },
  { field: "bookletsHrRate", label: "Curriculum (per hour)" },
  { field: "interviewRate", label: "Interview" },
] as const;
type HourlyPayRate = typeof hourlyPayRates[number];

const PayRatesModal: React.FC<{
  teacherId?: number;
  onClose: () => void;
}> = ({ teacherId, onClose }) => {
  const [getPayRates, { data, error }] = usePayRatesLazyQuery({
    fetchPolicy: "cache-and-network",
  });
  const [updatePayRates, { loading: isSaving }] = useUpdatePayRatesMutation();
  const [form] = Form.useForm();

  useEffect(() => {
    form.resetFields();
  }, [data, teacherId]);

  useEffect(() => {
    if (teacherId) {
      getPayRates({
        variables: {
          teacherId,
        },
      });
    }
  }, [teacherId]);

  useEffect(() => {
    if (error) {
      onClose();
      message.error(`Teacher's pay rates failed to load: ${error.message}`, 10);
    }
  }, [error]);

  const payField = (
    <InputNumber
      formatter={(value): string =>
        `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      }
      parser={(value): string => value?.replace(/\$\s?|(,*)/g, "") ?? "0"}
    />
  );

  const penaltyField = (
    <InputNumber
      min={0}
      max={MAX_PENALTY_RATE}
      formatter={(value): string => `${value} %`}
      parser={(value): string => value?.replace(/\s?%/g, "") ?? "0"}
    />
  );

  const flatPenaltyField = (
    <InputNumber
      min={0}
      precision={2}
      formatter={(value): string => `$ ${value}`}
      parser={(value): string => value?.replace(/\$\s?|(,*)/g, "") ?? "0"}
    />
  );

  return (
    <Modal
      visible={teacherId !== undefined}
      title="Pay Rates"
      width={1000}
      okText="Update"
      cancelText="Cancel"
      confirmLoading={isSaving}
      onCancel={onClose}
      onOk={async (): Promise<void> => {
        try {
          if (!teacherId) {
            return;
          }

          const values = await form.validateFields();
          values.intervalRates.forEach((intervalRate: TeacherIntervalRate) => {
            delete intervalRate.__typename;
          });
          console.log(values.intervalRates);
          const variables = {
            teacherId,
            input: values,
          };
          await updatePayRates({
            variables,
          });
          onClose();
          message.success("Teacher's pay rates successfully saved");
        } catch (error) {
          message.error(
            `Teacher's pay rates failed to save: ${error.message}`,
            10
          );
        }
      }}
    >
      <Form form={form} layout="vertical" initialValues={data?.getPayRates}>
        <Form.Item>
          All pay rates are in hours except where specified.
          <br />
          <br />
          Please note that the pay rates information is not shared between the
          differing instances of Cortex.
          <br />
          <br />
          It is not recommended changing the pay rates until the new term has
          started, as the timesheet history is calculated using the current pay
          rates.
        </Form.Item>
        <Form.Item>
          <h3>Hourly pay rates and flat penalty additions</h3>
          Each pay rate has its own flat addition for weekday evenings,
          Saturdays, and Sundays. Enter the extra dollars per eligible hour, or
          zero for no flat addition. Add the flat amount to the hourly rate
          first, then apply the percentage penalty configured below.
        </Form.Item>
        <Table<HourlyPayRate>
          dataSource={[...hourlyPayRates]}
          rowKey="field"
          pagination={false}
          size="small"
          scroll={{ x: 850 }}
          columns={[
            { title: "Pay rate", dataIndex: "label" },
            {
              title: "Base ($/hour)",
              render: (_: unknown, payRate: HourlyPayRate) => (
                <Form.Item
                  name={payRate.field}
                  rules={[{ required: true }]}
                  style={{ marginBottom: 0 }}
                >
                  {payField}
                </Form.Item>
              ),
            },
            ...[
              {
                period: "WeekdayEvening",
                label: "Weekday after 7 pm (+$/hour)",
              },
              { period: "Saturday", label: "Saturday (+$/hour)" },
              { period: "Sunday", label: "Sunday (+$/hour)" },
            ].map(({ period, label }) => ({
              title: label,
              render: (_: unknown, payRate: HourlyPayRate) => (
                <Form.Item
                  name={`${payRate.field}${period}PenaltyAmount`}
                  rules={[{ required: true, type: "number", min: 0 }]}
                  style={{ marginBottom: 0 }}
                >
                  {flatPenaltyField}
                </Form.Item>
              ),
            })),
          ]}
        />
        <br />
        <Form.Item
          name="classExtraRate"
          label="Class - extra per extra student"
          rules={[{ required: true }]}
        >
          {payField}
        </Form.Item>
        <Form.Item
          name="classExtraNumStudentsThreshold"
          label="Class - extra per extra student - kicks in at which student?"
          rules={[{ required: true }]}
        >
          <InputNumber />
        </Form.Item>
        <Form.Item
          name="bookletsBookRate"
          label="Curriculum (per booklet)"
          rules={[{ required: true }]}
        >
          {payField}
        </Form.Item>
        <Form.Item>
          <h3>Percentage penalty additions</h3>
          Percentage penalties apply to the hourly rate after its flat addition.
          Flat additions use the amounts in the matching row above. For example,
          a $40 class rate plus a $3 flat addition, then 25%, pays $53.75 per
          eligible hour: ($40 + $3) × 1.25. Assisting can have a different flat
          addition.
          <br />
          <br />
          Leave percentages and flat additions at zero when the usual hourly
          rate already covers penalties.
          <br />
          <br />
          Penalties use the class times recorded on the <b>Attendance</b> page
          or the date and start/end times of an extra activity. Old-format
          records without these times receive no penalty. Per-booklet payments
          and manually entered Other amounts are not hourly pay rates.
        </Form.Item>
        {[
          { label: "Weekday after 7:00 pm", field: "weekdayEvening" },
          { label: "Saturday", field: "saturday" },
          { label: "Sunday", field: "sunday" },
        ].map(({ label, field }) => (
          <div key={field}>
            <b>{label}</b>
            <br />
            <Space align="start" wrap>
              <Form.Item
                name={`${field}PenaltyRate`}
                label="Percentage addition"
                rules={[
                  {
                    required: true,
                    type: "number",
                    min: 0,
                    max: MAX_PENALTY_RATE,
                  },
                ]}
              >
                {penaltyField}
              </Form.Item>
            </Space>
          </div>
        ))}
        <Form.Item>
          <h3>Extra for students in class based on discrete intervals</h3>A
          discrete interval allows teachers to get paid extra per hour if the
          number of students in the class is within the range of <b>Start</b> to{" "}
          <b>End</b> inclusive. Please note that interval rates are only applied
          to classes.
          <br />
          If the interval you are looking at add has no upper bound (e.g. 4+
          students), then simply put a very large impossible number of students,
          such as 999, as the End value.
        </Form.Item>
        <Form.List name="intervalRates">
          {(intervalRates, { add, remove }): JSX.Element => {
            return (
              <>
                {intervalRates.map((intervalRate) => (
                  <Space key={intervalRate.key} align="start">
                    <Form.Item
                      {...intervalRate}
                      label="Start"
                      name={[intervalRate.name, "start"]}
                      fieldKey={[intervalRate.fieldKey, "start"]}
                      rules={[{ required: true }]}
                    >
                      <InputNumber />
                    </Form.Item>
                    <Form.Item
                      {...intervalRate}
                      label="End"
                      name={[intervalRate.name, "end"]}
                      fieldKey={[intervalRate.fieldKey, "end"]}
                      rules={[{ required: true }]}
                    >
                      <InputNumber />
                    </Form.Item>
                    <Form.Item
                      {...intervalRate}
                      label="Extra per hour"
                      name={[intervalRate.name, "rate"]}
                      fieldKey={[intervalRate.fieldKey, "rate"]}
                      rules={[{ required: true }]}
                    >
                      {payField}
                    </Form.Item>
                    <MinusCircleOutlined
                      onClick={(): void => remove(intervalRate.name)}
                    />
                  </Space>
                ))}

                <Form.Item>
                  <Button type="dashed" onClick={(): void => add()} block>
                    <PlusOutlined /> New interval
                  </Button>
                </Form.Item>
              </>
            );
          }}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default PayRatesModal;
